import React, { useState, useEffect, useRef } from 'react';
import { ShieldAlert, Shield, Copyright, Lock, KeyRound, Eye, EyeOff, AlertTriangle, Loader2, Sparkles, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import SecurityManagementPage from './SecurityManagementPage';
import DMCAManagementPage from './DMCAManagementPage';
import HubShell from '../../components/staff/HubShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const UNLOCK_KEY = 'trust_safety_unlock_until';

/**
 * Trust & Safety Hub — bundles Security Centre and DMCA/copyright compliance.
 * Both surfaces are protective/legal in nature so they belong together.
 * Guarded by a second-factor passcode (TRUST_SAFETY_PASSCODE) per session.
 */
const TrustSafetyHubPage = (props) => {
  const { securityAccess, dmcaAccess, getAuthHeaders } = props;
  const first = securityAccess ? 'security' : (dmcaAccess ? 'dmca' : 'security');
  const [tab, setTab] = useState(first);

  // Passcode gate state
  const [unlockUntil, setUnlockUntil] = useState(() => {
    const v = Number(sessionStorage.getItem(UNLOCK_KEY) || 0);
    return v > Date.now() ? v : 0;
  });
  const isUnlocked = unlockUntil > Date.now();

  const tabs = [
    { id: 'security', label: 'Security Centre', icon: Shield,    desc: 'Threats, lockdown & audit logs', visible: !!securityAccess, testId: 'hub-tab-security' },
    { id: 'dmca',     label: 'DMCA',            icon: Copyright, desc: 'Takedown & copyright compliance', visible: !!dmcaAccess,     testId: 'hub-tab-dmca' },
  ];

  // Auto-expire the unlock window
  useEffect(() => {
    if (!isUnlocked) return;
    const ms = Math.max(0, unlockUntil - Date.now());
    const t = setTimeout(() => {
      sessionStorage.removeItem(UNLOCK_KEY);
      setUnlockUntil(0);
    }, ms);
    return () => clearTimeout(t);
  }, [unlockUntil, isUnlocked]);

  if (!securityAccess && !dmcaAccess) {
    return (
      <div className="flex items-center justify-center h-[70vh]" data-testid="trust-safety-denied">
        <div className="text-center">
          <ShieldAlert className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-light text-white tracking-tight mb-1">Restricted</h2>
          <p className="text-white/40 text-sm">You don't have access to Trust & Safety.</p>
        </div>
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <TrustSafetyLockScreen
        onUnlocked={(until) => {
          sessionStorage.setItem(UNLOCK_KEY, String(until));
          setUnlockUntil(until);
        }}
        getAuthHeaders={getAuthHeaders}
      />
    );
  }

  return (
    <HubShell
      title="Trust &amp; Safety"
      accent="Hub"
      subtitle="Everything that keeps the platform safe and compliant."
      icon={ShieldAlert}
      tone="violet"
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      dataTestId="trust-safety-hub"
      headerRight={<UnlockCountdown until={unlockUntil} onLock={() => { sessionStorage.removeItem(UNLOCK_KEY); setUnlockUntil(0); }} />}
    >
      {tab === 'security' && securityAccess && <SecurityManagementPage {...props} />}
      {tab === 'dmca'     && dmcaAccess     && <DMCAManagementPage {...props} />}
    </HubShell>
  );
};

// ============================================================
// Countdown pill — shown in the hub header after unlock
// ============================================================
const UnlockCountdown = ({ until, onLock }) => {
  const [remaining, setRemaining] = useState(() => Math.max(0, until - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setRemaining(Math.max(0, until - Date.now())), 1000);
    return () => clearInterval(t);
  }, [until]);
  const mm = String(Math.floor(remaining / 60000)).padStart(2, '0');
  const ss = String(Math.floor((remaining % 60000) / 1000)).padStart(2, '0');
  return (
    <button
      onClick={onLock}
      title="Lock Trust & Safety now"
      data-testid="trust-safety-relock-btn"
      className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 hover:bg-emerald-500/15 hover:ring-emerald-500/50 transition-all text-[11px] font-semibold text-emerald-300"
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      <span className="tabular-nums tracking-wider">{mm}:{ss}</span>
      <span className="text-emerald-400/50 group-hover:text-emerald-300">· Lock</span>
    </button>
  );
};

// ============================================================
// Lock Screen — Jewel & Luxury passcode gate
// ============================================================
const TrustSafetyLockScreen = ({ onUnlocked, getAuthHeaders }) => {
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!passcode || loading) return;
    setLoading(true);
    setError('');
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/trust-safety/unlock`,
        { passcode },
        { headers }
      );
      if (res.data?.ok && res.data?.unlock_until) {
        onUnlocked(res.data.unlock_until);
      } else {
        setError('Unexpected response');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Verification failed';
      setError(detail);
      setAttempts((a) => a + 1);
      setPasscode('');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] flex items-center justify-center px-4 py-10 overflow-hidden" data-testid="trust-safety-lock-screen">
      {/* Ambient background */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-16 w-96 h-96 rounded-full blur-3xl bg-violet-600/15" />
        <div className="absolute -bottom-40 -right-16 w-[520px] h-[520px] rounded-full blur-3xl bg-cyan-500/10" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      <div className={`relative w-full max-w-md ${shake ? 'animate-[shake_0.45s_cubic-bezier(.36,.07,.19,.97)]' : ''}`}>
        <style>{`
          @keyframes shake {
            10%, 90% { transform: translateX(-1px); }
            20%, 80% { transform: translateX(2px); }
            30%, 50%, 70% { transform: translateX(-4px); }
            40%, 60% { transform: translateX(4px); }
          }
          @keyframes shine {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>

        {/* Conic gradient ring around card */}
        <div
          aria-hidden
          className="absolute -inset-[1px] rounded-[26px] opacity-70 blur-[1px]"
          style={{
            background: 'conic-gradient(from 90deg, transparent 0deg, #8B5CF6 60deg, transparent 140deg, #00E5FF 220deg, transparent 300deg)',
            animation: 'spin 8s linear infinite',
          }}
        />

        <div className="relative bg-gradient-to-b from-slate-950/95 via-slate-950/98 to-slate-950 backdrop-blur-2xl rounded-[24px] p-8 ring-1 ring-white/5 shadow-[0_30px_120px_-20px_rgba(139,92,246,0.35)]">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="relative mb-4">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40 blur-2xl scale-125" />
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 p-[2px] shadow-2xl shadow-violet-500/30">
                <div className="w-full h-full rounded-[14px] bg-slate-950 flex items-center justify-center">
                  <Lock className="w-7 h-7 text-white" strokeWidth={2.2} />
                </div>
              </div>
              {/* Floating shield crest */}
              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center ring-[2.5px] ring-slate-950 shadow-lg shadow-amber-500/40">
                <ShieldAlert className="w-3 h-3 text-white" />
              </div>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-violet-400/80 mb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Restricted Zone
            </p>
            <h1 className="text-2xl font-bold text-white tracking-tight mb-1.5">
              Trust &amp; Safety Vault
            </h1>
            <p className="text-slate-400 text-[13px] max-w-xs leading-relaxed">
              Enter the Trust &amp; Safety passcode to unlock Security Centre &amp; DMCA controls for the next 30 minutes.
            </p>
          </div>

          {/* Passcode form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">
                <KeyRound className="w-3 h-3" />
                Vault Passcode
              </label>
              <div className={`relative group ${error ? 'ring-1 ring-rose-500/40 rounded-xl' : ''}`}>
                <input
                  ref={inputRef}
                  type={showPasscode ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => { setPasscode(e.target.value); if (error) setError(''); }}
                  disabled={loading}
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="trust-safety-passcode-input"
                  placeholder="••••••••••••••••••"
                  className="w-full bg-slate-900/60 ring-1 ring-slate-800 hover:ring-slate-700 focus:ring-violet-500/50 focus:bg-slate-900 rounded-xl pl-4 pr-11 py-3.5 text-white text-[15px] tracking-widest font-medium placeholder-slate-700 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPasscode((s) => !s)}
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-500 hover:text-violet-300 hover:bg-slate-800/60 transition-all"
                  title={showPasscode ? 'Hide' : 'Reveal'}
                  data-testid="trust-safety-toggle-visibility"
                >
                  {showPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/10 ring-1 ring-rose-500/30 px-3.5 py-2.5" data-testid="trust-safety-error">
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                <span className="text-[12px] leading-relaxed text-rose-200 font-medium">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!passcode || loading}
              data-testid="trust-safety-unlock-btn"
              className="relative w-full py-3.5 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-semibold text-[14px] tracking-wide shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-violet-500/30 flex items-center justify-center gap-2 overflow-hidden group"
            >
              <span
                aria-hidden
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] opacity-0 group-hover:opacity-100"
                style={{ animation: 'shine 2s linear infinite' }}
              />
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying…
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Unlock Trust &amp; Safety
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-5 border-t border-slate-800/60 flex items-center justify-between text-[10px] font-medium text-slate-500 uppercase tracking-[0.14em]">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)] animate-pulse" />
              Zero-Trust Session
            </span>
            <span>{attempts > 0 ? `Attempts: ${attempts}` : 'AES-256 · Audited'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrustSafetyHubPage;
